import { BytesPerMegaByte, UploadMaxFileSizes } from '@tempo-io/fim3-dto';
import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import {
  AddCircle,
  AttachFile,
  Delete,
  Download,
  PictureAsPdf,
  UploadFile,
} from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { type ChangeEvent, type DragEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSetSnackbarMessage } from '../../context/SnackbarContext';
import { getSafeName, getSafeUrl } from '../../utils/Utilities';
import { DeleteAttachmentDialog } from './DeleteAttachmentDialog';
import {
  DEFAULT_ACCEPTED_FILE_TYPES,
  formatAcceptedFileTypes,
  useAttachmentValidation,
} from './useAttachmentValidation';

export type AttachmentBase = {
  name: string;
  id: number;
  createdAt: Date;
  attachmentKey: string;
  attachmentUrl: string;
  mimeType: string;
  size: number;
};

export type AttachmentsProps = {
  hideTitle?: boolean;
  attachments: AttachmentBase[];
  updateAttachment?: (file: File, filePath: string) => void;
  deleteAttachment?: (id: number) => void;
  disabled?: boolean;
  acceptedFileTypes?: string[];
};

const getThumbnail = (mimeType: string, safeUrl: string | null, safeName: string) => {
  if (mimeType === 'application/pdf') {
    return <PictureAsPdf size={44} />;
  }
  if (mimeType.startsWith('image/') && safeUrl) {
    return <img src={safeUrl} alt={safeName} />;
  }
  return <AttachFile size={44} />;
};

const AttachmentRow = ({
  attachment,
  disabled,
  onDeleteClick,
}: {
  attachment: AttachmentBase;
  disabled?: boolean;
  onDeleteClick: (attachment: AttachmentBase) => void;
}) => {
  const { t } = useTranslation('translation');
  const safeUrl = getSafeUrl(attachment.attachmentUrl);
  const safeName = getSafeName(attachment.name);
  const thumbnail = getThumbnail(attachment.mimeType, safeUrl, safeName);

  return (
    <div className="flex gap-4 group">
      <div
        className="h-[60px] w-[60px] rounded-sm bg-bg-neutral p-2 [&_img]:h-11 [&_img]:w-11 [&_img]:object-contain fs-exclude"
        data-dd-action-name="FIM 3 Attachment - thumbnail redacted"
      >
        {thumbnail}
      </div>
      <p
        className="fs-exclude text-sm leading-normal grow"
        data-dd-action-name="FIM 3 Attachment - name redacted"
      >
        {safeName}
      </p>
      <div>
        {safeUrl && (
          <a href={safeUrl} target="_blank" rel="noopener noreferrer">
            <Button
              variant="ghost"
              size="icon-sm"
              className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
              aria-label={t(($) => $.intake.a11y.downloadAttachment)}
            >
              <Download className="size-6 text-icon-neutral-secondary" />
            </Button>
          </a>
        )}
        {!disabled && (
          <Button
            variant="ghost"
            size="icon-sm"
            className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
            aria-label={t(($) => $.intake.a11y.deleteAttachment)}
            onClick={() => onDeleteClick(attachment)}
          >
            <Delete className="size-6 text-icon-neutral-secondary" />
          </Button>
        )}
      </div>
    </div>
  );
};

export const Attachments = ({
  hideTitle,
  attachments,
  updateAttachment,
  deleteAttachment,
  disabled,
  acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
}: AttachmentsProps) => {
  const { t } = useTranslation('translation');

  const setSnackbarMessage = useSetSnackbarMessage();

  const canUpload = !!updateAttachment && !disabled;

  const [isDragging, setDragging] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AttachmentBase | null>(null);

  const formattedFileTypes = formatAcceptedFileTypes(acceptedFileTypes);
  const validateAttachment = useAttachmentValidation(acceptedFileTypes);

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragExit = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    handleDragExit(event);

    if (!event.dataTransfer.files) return;

    const file = event.dataTransfer.files[0];

    handleFileChange(file);
  };

  const handleFileChange = (file?: File | null) => {
    if (!updateAttachment || !file) return;

    const rejection = validateAttachment(file);
    if (rejection) {
      setSnackbarMessage({ message: rejection, severity: 'error' });
      return;
    }

    updateAttachment(file, file.name);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!updateAttachment || !event.target.files) return;
    const file = event.target.files?.[0];

    handleFileChange(file);

    event.target.value = '';
  };

  return (
    <div className="flex flex-col gap-2">
      {!hideTitle && (
        <div className="flex justify-between items-center">
          <span className="capitalize text-sm font-medium">
            {t(($) => $.intake.general.attachments)}
          </span>
          {canUpload && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon-sm" className="relative">
                    <AddCircle className="text-icon-info" />
                    <input
                      aria-label={t(($) => $.intake.a11y.uploadFile)}
                      accept={acceptedFileTypes.join(',')}
                      type="file"
                      onChange={handleInputChange}
                      data-testid="file-input"
                      className="opacity-0 absolute left-0 w-full h-full cursor-pointer"
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t(($) => $.intake.general.uploadAttachments)}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
      {canUpload && (
        <section
          className={cn(
            'border border-dashed border-border-neutral-default rounded flex flex-col items-center p-2 gap-2 relative',
            isDragging ? 'bg-bg-neutral-subtle' : 'bg-transparent',
          )}
        >
          <input
            aria-label={t(($) => $.intake.a11y.uploadFileArea)}
            accept={acceptedFileTypes.join(',')}
            type="file"
            onChange={handleInputChange}
            onDragOver={handleDragOver}
            onDragLeave={handleDragExit}
            onDrop={handleDrop}
            data-testid="file-input-drag-drop-area"
            className="opacity-0 absolute left-0 w-full h-full cursor-pointer"
          />
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t(($) => $.intake.a11y.uploadFileButton)}
              className="relative"
            >
              <UploadFile className="text-icon-neutral-primary size-6" />
              <input
                aria-label={t(($) => $.intake.a11y.uploadFileButton)}
                accept={acceptedFileTypes.join(',')}
                type="file"
                onChange={handleInputChange}
                data-testid="file-input-drag-drop-button"
                className="opacity-0 absolute left-0 w-full h-full cursor-pointer"
              />
            </Button>
            <p className="text-sm leading-normal font-normal">
              <Trans
                i18nKey="intake.general.clickToUploadOrDragAndDrop"
                components={{
                  span: <span className="underline" />,
                }}
              />
            </p>
          </div>
          <p className="text-xs text-text-neutral-secondary">
            {t(($) => $.intake.general.upload.supportedFileTypesAndSize, {
              acceptedFileTypes: formattedFileTypes,
              maxSize: UploadMaxFileSizes.attachment / BytesPerMegaByte,
            })}
          </p>
        </section>
      )}
      {attachments.map((attachment) => (
        <AttachmentRow
          disabled={disabled || !deleteAttachment}
          key={attachment.id}
          attachment={attachment}
          onDeleteClick={setPendingDelete}
        />
      ))}
      <DeleteAttachmentDialog
        attachmentId={pendingDelete?.id}
        onClose={() => setPendingDelete(null)}
        onConfirm={(attachmentId) => {
          deleteAttachment?.(attachmentId);
          setPendingDelete(null);
        }}
      />
    </div>
  );
};
